// Modal primitive — the design's centered 480px card over a blurred backdrop.
// Backdrop click and Escape close it; the card stops propagation.

import {
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { IconClose } from './icons';

interface Props {
  title?: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}

export function Modal({ title, onClose, children, width = 480 }: Props) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onBackdrop = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );
  const stop = useCallback((e: MouseEvent | KeyboardEvent) => e.stopPropagation(), []);
  const widthStyle = useMemo(() => ({ width }), [width]);

  return (
    <div className="bz-modal-backdrop" onClick={onBackdrop} role="presentation">
      <div
        ref={cardRef}
        className="bz-modal"
        style={widthStyle}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={stop}
      >
        {title && (
          <div className="bz-modal-head">
            <h3 className="bz-modal-title">{title}</h3>
            <button type="button" className="bz-modal-close" onClick={onClose} aria-label="Close">
              <IconClose size={15} />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
